import React, { useState, useRef } from 'react';
import { useUserUploads } from '../hooks/useUserUploads';

interface ImageUploaderProps {
  onUploadSuccess?: () => void;
  maxSizeMB?: number;
  allowedTypes?: string[];
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  onUploadSuccess,
  maxSizeMB = 5, // Default max size 5MB
  allowedTypes = ['image/jpeg', 'image/png', 'image/webp']
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { uploadImage } = useUserUploads();
  
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  
  // Handle file validation
  const validateFile = (file: File): string | null => {
    if (!allowedTypes.includes(file.type)) {
      return `File type not supported. Please upload ${allowedTypes.join(', ')} files only.`;
    }
    
    if (file.size > maxSizeBytes) {
      return `File is too large. Maximum size is ${maxSizeMB}MB.`;
    }
    
    return null;
  };
  
  // Handle file upload
  const handleUpload = async (file: File) => {
    // Reset state
    setError(null);
    setProgress(0);
    
    // Validate file
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setUploading(true);
    
    try {
      // Create a fake progress animation
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          const next = prev + Math.random() * 10;
          return next > 90 ? 90 : next;
        });
      }, 300);
      
      // Upload the file
      const result = await uploadImage(file);
      
      // Clear the interval
      clearInterval(progressInterval);
      
      if (result) {
        setProgress(100);
        if (onUploadSuccess) {
          onUploadSuccess();
        }
        
        // Reset after 1 second
        setTimeout(() => {
          setProgress(0);
          setUploading(false);
        }, 1000);
      } else {
        throw new Error('Upload failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload image');
      setProgress(0);
    } finally {
      setUploading(false);
    }
  };
  
  // Handle drag events
  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  
  // Handle drop event
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUpload(e.dataTransfer.files[0]);
    }
  };
  
  // Handle file input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleUpload(e.target.files[0]);
    }
  };
  
  // Handle button click to open file dialog
  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  return (
    <div 
      className={`border-2 border-dashed rounded-lg p-6 text-center 
        ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'} 
        transition-colors cursor-pointer relative`}
      onDragEnter={handleDrag}
      onDragOver={handleDrag}
      onDragLeave={handleDrag}
      onDrop={handleDrop}
      onClick={handleButtonClick}
    >
      <input 
        type="file" 
        ref={fileInputRef}
        className="hidden"
        accept={allowedTypes.join(',')}
        onChange={handleChange}
        disabled={uploading}
      />
      
      <div className="flex flex-col items-center justify-center space-y-3">
        {/* Icon */}
        <div className={`w-12 h-12 rounded-full ${dragActive ? 'bg-blue-100' : 'bg-gray-100'} flex items-center justify-center`}>
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className={`h-6 w-6 ${dragActive ? 'text-blue-500' : 'text-gray-500'}`} 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
        </div>
        
        {/* Text */}
        <div>
          <p className="font-medium text-sm">
            {uploading ? 'Uploading...' : 'Drag and drop an image or click to browse'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {allowedTypes.map(type => type.replace('image/', '.')).join(', ')} files up to {maxSizeMB}MB
          </p>
        </div>
        
        {/* Error message */}
        {error && (
          <div className="text-red-500 text-xs font-medium">
            {error}
          </div>
        )}
        
        {/* Progress bar */}
        {uploading && (
          <div className="w-full max-w-xs bg-gray-200 rounded-full h-2.5 overflow-hidden">
            <div 
              className="bg-blue-600 h-2.5 transition-all duration-300 ease-out" 
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUploader; 